import React from "react";
import { Button } from "@mui/material";

import AlertDialog from "../common/AlertDialog";
import {
  ensureCallPilotChatbotReady,
  openCallPilotChatbot,
} from "../helpers/callPilotChatbot";
import {
  PROPERTY_RISK_SUPPORT_PHONE,
  PROPERTY_RISK_SUPPORT_PHONE_FORMAT,
  TAILWIND_BUTTON_BASE_CLASS,
  TAILWIND_SUCCESS_BUTTON_RING_CLASS,
} from "../helpers/constants";

export const RISK_WALKTHROUGH_VIDEO_SRC =
  "https://www.youtube.com/embed/P5b_8nDAdcc?rel=0";

/**
 * Recovery dialog when Benutech cannot uniquely match a risk-assessment address.
 * Primary: return to the form. Secondary: open CallPilot (Sara) for sales help.
 */
export default function PropertyLocateRecoveryDialog({
  open,
  onTryAgain,
  isTailwindComponent = true,
  errorCode,
  searchType = "address",
}) {
  if (!open) {
    return null;
  }

  const needsUnit = errorCode === "unit_required";
  const isParcel = searchType === "parcel";

  const handleNeedHelp = () => {
    void ensureCallPilotChatbotReady().then(() => {
      openCallPilotChatbot();
    });
    onTryAgain?.();
  };

  return (
    <AlertDialog
      isTailwindComponent={isTailwindComponent}
      type="Warning"
      width="md"
      openPopup
      onClose
      onCloseFun={onTryAgain}
      title="Let’s Try That Address Again"
      footer={false}
      mesage={
        <>
          <p>
            We couldn’t match the {isParcel ? "parcel number" : "address"} you
            entered with a valid property record. Let&apos;s check for a missing
            unit number, confirm the ZIP code, or try the address as it appears
            on your property tax statement.
          </p>
          {needsUnit ? (
            <p className="pt-3 font-semibold">
              Multiple properties match this street address. Add your unit or
              condo number, then try again.
            </p>
          ) : null}
          <div className="pt-4">
            {/* Capped so the video doesn't dominate the md-width dialog */}
            <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-xl aspect-video bg-black/5">
              <iframe
                className="absolute inset-0 h-full w-full"
                src={RISK_WALKTHROUGH_VIDEO_SRC}
                title="How the property risk scan works"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <p className="pt-4 text-sm text-muted-foreground">
            If the issue persists, our team of specialists are available to
            help. Call{" "}
            <a
              href={`tel:${PROPERTY_RISK_SUPPORT_PHONE}`}
              className="contact_us_texts"
            >
              {PROPERTY_RISK_SUPPORT_PHONE_FORMAT}
            </a>
            .
          </p>
        </>
      }
      buttons={
        <>
          <Button
            className={`m-2 ${
              isTailwindComponent
                ? TAILWIND_BUTTON_BASE_CLASS
                : "popup_Btn other_popup_btn"
            }`}
            variant="outlined"
            onClick={handleNeedHelp}
          >
            I Need Help
          </Button>
          <Button
            className={`m-2 ${
              isTailwindComponent
                ? TAILWIND_SUCCESS_BUTTON_RING_CLASS
                : "popup_Btn success_popup_btn"
            }`}
            variant="contained"
            onClick={onTryAgain}
          >
            Try Again
          </Button>
        </>
      }
    />
  );
}
